import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Computer as ComputerIcon,
  Terminal as TerminalIcon,
  Code as CodeIcon,
  Cloud as CloudConnectIcon,
  SettingsApplications as ConfigIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  CopyAll as CopyIcon,
  CheckCircle as CheckIcon,
  Settings as SettingsIcon,
  CloudSync as CloudIcon,
  Extension as ApiIcon,
  Security as SecurityIcon,
  BugReport as BugIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

function MCPHelp({ darkMode }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [copiedText, setCopiedText] = useState('');

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  function TabPanel({ children, value, index }) {
    return (
      <div hidden={value !== index}>
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  function CodeBlock({ children, language = 'bash', copyable = true }) {
    return (
      <Paper
        sx={{
          p: 2,
          backgroundColor: darkMode ? '#1e1e1e' : '#f5f5f5',
          position: 'relative',
          fontFamily: 'monospace',
        }}
      >
        {copyable && (
        <IconButton
          size="small"
          onClick={() => copyToClipboard(children, 'code')}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          {copiedText === 'code' ? <CheckIcon color="success" /> : <CopyIcon />}
        </IconButton>
        )}
        <pre style={{ margin: 0, overflow: 'auto' }}>
          <code>{children}</code>
        </pre>
      </Paper>
    );
  }

  function ConfigBlock({ title, config, copyable = true }) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">{title}</Typography>
            {copyable && (
            <IconButton
              size="small"
              onClick={() => copyToClipboard(JSON.stringify(config, null, 2), title)}
            >
              {copiedText === title ? <CheckIcon color="success" /> : <CopyIcon />}
            </IconButton>
            )}
          </Box>
          <CodeBlock copyable={false}>
            {JSON.stringify(config, null, 2)}
          </CodeBlock>
        </CardContent>
      </Card>
    );
  }

  const connectionInstructions = [
    {
      title: '1. Start Your Backend Server',
      description: 'First, ensure your agentic flows backend is running',
      steps: [
        'Open a terminal and navigate to the backend directory',
        'Start the server using one of these methods:',
      ],
      code: [
        '# Method 1: Using main.py (recommended)',
        'cd backend',
        'python main.py',
        '',
        '# Method 2: Using development server',
        'python dev_server.py',
        '',
        '# Method 3: Using uvicorn directly',
        'uvicorn main:app --host 0.0.0.0 --port 8001 --reload',
      ],
    },
    {
      title: '2. Your Agentic Flows as MCP Tools',
      description: 'Your agentic flows are automatically exposed as MCP tools',
      steps: [
        'All your agentic flows become available as MCP tools',
        "Each flow gets a user-friendly name (e.g., 'testFlow', 'firstFlow')",
        'Tools are automatically loaded when you connect to the MCP server',
        'Use flows directly in chat conversations through the MCP Tools dropdown',
      ],
      code: [
        '# Example: Your flows become tools like:',
        'testFlow - This is the first flow I created',
        'test2Flow - This is the second flow I created',
        'firstFlow - This is the real first flow for testing purposes',
        '',
        '# Access them through:',
        'Form Options Drawer → MCP Server → MCP Tools dropdown',
      ],
    },
    {
      title: '3. Verify MCP Server is Running',
      description: 'Check that the MCP server is accessible',
      steps: [
        'Test the MCP server connection using the built-in client',
        'Verify that flows are being exposed as MCP tools',
      ],
      code: [
        '# Test MCP server',
        'cd backend',
        'python mcp_client.py websocket --test',
        '',
        '# Interactive testing',
        'python mcp_client.py websocket --interactive',
      ],
    },
    {
      title: '4. Configure MCP Clients',
      description: 'Set up your preferred MCP client to connect to your server',
    },
  ];

  const clientConfigs = {
    claudeDesktop: {
      name: 'Claude Desktop',
      description: 'Connect to Claude Desktop for AI-powered flow execution',
      config: {
        mcpServers: {
          'agentic-flows': {
            command: 'python',
            args: ['/path/to/your/backend/mcp_server.py'],
            env: {
              MONGODB_URI: 'mongodb://localhost:27017',
              MCP_LOG_LEVEL: 'INFO',
            },
          },
        },
      },
      setup: [
        'Open Claude Desktop',
        'Go to Settings > Developer > Edit Config',
        'Add the configuration above to your config file',
        'Restart Claude Desktop',
        'Your flows will appear as available tools',
      ],
    },
    vscode: {
      name: 'VS Code MCP Extension',
      description: 'Connect to VS Code for development workflow integration',
      config: {
        mcp: {
          servers: {
            'agentic-flows': {
              command: 'python',
              args: ['/path/to/your/backend/mcp_server.py'],
              env: {
                MONGODB_URI: 'mongodb://localhost:27017',
              },
            },
          },
        },
      },
      setup: [
        'Install the MCP extension for VS Code',
        'Create or edit your MCP configuration file',
        'Add the configuration above',
        'Reload VS Code',
        'Access flows through the MCP panel',
      ],
    },
    custom: {
      name: 'Custom MCP Client',
      description: 'Connect using a custom MCP client implementation',
      config: {
        transport: 'websocket',
        host: 'localhost',
        port: 8001,
        path: '/api/v1/mcp',
      },
      setup: [
        'Implement MCP client using WebSocket transport',
        'Connect to ws://localhost:8001/api/v1/mcp/ws',
        'Send JSON-RPC messages following MCP protocol',
        'Use the available tools and resources',
      ],
    },
  };

  const availableTools = [
    {
      name: 'Flow Execution',
      tools: [
        { name: 'execute_flow_{flow_id}', description: 'Execute a specific agentic flow' },
        { name: 'list_flows', description: 'List all available flows with filtering' },
        { name: 'search_flows', description: 'Advanced semantic search for flows' },
        { name: 'get_flow_status', description: 'Check execution status of a flow' },
        { name: 'cancel_flow_execution', description: 'Cancel a running flow execution' },
      ],
    },
    {
      name: 'Flow Management',
      tools: [
        { name: 'get_flow_templates', description: 'List available flow templates' },
        { name: 'create_flow_from_template', description: 'Create new flows from templates' },
        { name: 'validate_flow', description: 'Validate flow definitions' },
        { name: 'get_flow_statistics', description: 'Get execution statistics' },
      ],
    },
  ];

  const troubleshooting = [
    {
      issue: 'MCP server not starting',
      symptoms: ['Connection refused', 'Port already in use', 'Permission denied'],
      solutions: [
        'Check if another service is using port 8001: `lsof -i :8001`',
        "Kill existing processes: `pkill -f 'python.*main.py'`",
        'Try a different port by setting PORT environment variable',
        'Ensure you have proper permissions to bind to the port',
      ],
    },
    {
      issue: 'Tools not appearing in MCP client',
      symptoms: ['Empty tools list', 'Connection successful but no tools'],
      solutions: [
        'Restart your MCP client after configuration changes',
        'Check that flows exist in your MongoDB database',
        'Verify MCP server logs for errors',
        'Test with the built-in MCP client first',
      ],
    },
    {
      issue: 'Connection errors',
      symptoms: ['WebSocket connection failed', 'Timeout errors', 'Authentication failed'],
      solutions: [
        'Verify your backend server is running and accessible',
        'Check firewall settings and network connectivity',
        'Ensure MongoDB is running and accessible',
        'Check MCP server logs for detailed error messages',
      ],
    },
    {
      issue: 'Flow execution failures',
      symptoms: ['Execution timeouts', 'Invalid flow format', 'Missing dependencies'],
      solutions: [
        'Check flow definition format and validation',
        'Ensure all required AI models are available',
        'Verify flow dependencies and external services',
        'Check execution logs for specific error details',
      ],
    },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom color="primary">
        MCP (Model Context Protocol) Help
      </Typography>

      <Typography variant="body1" paragraph>
        The Model Context Protocol (MCP) allows you to connect your agentic flows to various AI clients
        and development tools. This guide will help you set up MCP connections and troubleshoot common issues.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>What is MCP?</strong>
          {' '}
          MCP is a protocol that enables AI applications to securely connect
          to data sources and tools. Your agentic flows are exposed as MCP tools that can be used by
          Claude Desktop, VS Code, and other MCP-compatible clients.
        </Typography>
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label="Getting Started" icon={<PlayIcon />} />
          <Tab label="Agentic Flows" icon={<CodeIcon />} />
          <Tab label="Client Setup" icon={<ConfigIcon />} />
          <Tab label="Available Tools" icon={<ApiIcon />} />
          <Tab label="Troubleshooting" icon={<BugIcon />} />
          <Tab label="Advanced" icon={<SettingsIcon />} />
        </Tabs>
      </Box>

      {/* Getting Started Tab */}
      <TabPanel value={selectedTab} index={0}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Quick Start Guide
            </Typography>
            <Typography variant="body1" paragraph>
              Follow these steps to connect your agentic flows to MCP clients.
            </Typography>
          </Box>

          {connectionInstructions.map((instruction, index) => (
            <Card key={index} elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {instruction.title}
                </Typography>
                <Typography variant="body2" paragraph>
                  {instruction.description}
                </Typography>

                {instruction.steps && (
                  <List dense>
                    {instruction.steps.map((step, stepIndex) => (
                      <ListItem key={stepIndex}>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={step} />
                      </ListItem>
                    ))}
                  </List>
                )}

                {instruction.code && (
                  <Box mt={2}>
                    <CodeBlock>{instruction.code.join('\n')}</CodeBlock>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}

          <Alert severity="success">
            <Typography variant="body2">
              <strong>Success!</strong>
              {' '}
              Once you've completed these steps, your agentic flows will be
              available as tools in your MCP client. You can execute flows, manage templates, and
              access flow resources directly from your AI assistant or development environment.
            </Typography>
          </Alert>
        </Stack>
      </TabPanel>

      {/* Agentic Flows Tab */}
      <TabPanel value={selectedTab} index={1}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Using Your Agentic Flows as MCP Tools
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your agentic flows are automatically exposed as MCP tools, making them accessible
              directly in chat conversations and through external MCP clients.
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Key Feature:</strong>
              {' '}
              All your agentic flows become MCP tools with user-friendly names
              instead of cryptic IDs. Access them through the MCP Tools dropdown in the chat interface.
            </Typography>
          </Alert>

          <Box>
            <Typography variant="h6" gutterBottom>
              How It Works
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Automatic Tool Generation"
                  secondary="Every agentic flow you create automatically becomes an MCP tool"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="User-Friendly Names"
                  secondary="Tools use your flow names (e.g., 'testFlow', 'firstFlow') instead of cryptic IDs"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Rich Descriptions"
                  secondary="Each tool includes your flow description for better understanding"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Seamless Integration"
                  secondary="Use flows directly in chat conversations through the MCP Tools dropdown"
                />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Accessing Your Flow Tools
            </Typography>
            <Typography variant="body2" paragraph>
              To use your agentic flows as MCP tools:
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="1. Open the Form Options Drawer"
                  secondary="Click the settings icon in the chat interface"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. Select an MCP Server"
                  secondary="Choose your local MCP server from the dropdown"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Choose a Flow Tool"
                  secondary="Select from your available flows in the MCP Tools dropdown"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="4. Start Chatting"
                  secondary="Your selected flow will be used as a tool in the conversation"
                />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Example Flow Tools
            </Typography>
            <Typography variant="body2" paragraph>
              Your flows appear as tools like this:
            </Typography>
            <CodeBlock copyable={false}>
              {`testFlow - This is the first flow I created
test2Flow - This is the second flow I created  
firstFlow - This is the real first flow for testing purposes
customerOnboarding - Automated customer onboarding workflow
leadQualification - Lead scoring and qualification process`}
            </CodeBlock>
          </Box>

          <Alert severity="success">
            <Typography variant="body2">
              <strong>Pro Tip:</strong>
              {' '}
              You can combine multiple flow tools in a single conversation.
              Select different tools as needed, and the AI will use them together to provide
              comprehensive responses.
            </Typography>
          </Alert>
        </Stack>
      </TabPanel>

      {/* Client Setup Tab */}
      <TabPanel value={selectedTab} index={2}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" gutterBottom>
              MCP Client Configuration
            </Typography>
            <Typography variant="body1" paragraph>
              Choose your preferred MCP client and follow the setup instructions below.
            </Typography>
          </Box>

          {Object.entries(clientConfigs).map(([key, client]) => (
            <Card key={key} elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <CloudConnectIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">{client.name}</Typography>
                </Box>

                <Typography variant="body2" paragraph>
                  {client.description}
                </Typography>

                <Typography variant="subtitle1" gutterBottom>
                  Configuration:
                </Typography>
                <ConfigBlock title={`${client.name} Config`} config={client.config} />

                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Setup Steps:
                </Typography>
                <List dense>
                  {client.setup.map((step, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: 24,
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: 'primary.main',
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </ListItemIcon>
                      <ListItemText primary={step} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ))}

          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Important:</strong>
              {' '}
              Make sure to replace `/path/to/your/backend/mcp_server.py`
              with the actual path to your backend directory in the configuration files.
            </Typography>
          </Alert>
        </Stack>
      </TabPanel>

      {/* Available Tools Tab */}
      <TabPanel value={selectedTab} index={2}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Available MCP Tools
            </Typography>
            <Typography variant="body1" paragraph>
              Your agentic flows are exposed as MCP tools that can be used by AI assistants and
              development environments. Here are the available tools and their descriptions.
            </Typography>
          </Box>

          {availableTools.map((category, index) => (
            <Card key={index} elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {category.name}
                </Typography>

                <List>
                  {category.tools.map((tool, toolIndex) => (
                    <ListItem key={toolIndex} divider>
                      <ListItemIcon>
                        <ApiIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={(
                          <Box>
                            <Typography variant="subtitle1" component="code">
                              {tool.name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(tool.name, tool.name)}
                              sx={{ ml: 1 }}
                            >
                              {copiedText === tool.name ? <CheckIcon color="success" /> : <CopyIcon />}
                            </IconButton>
                          </Box>
                        )}
                        secondary={tool.description}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ))}

          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage Examples
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                In Claude Desktop:
              </Typography>
              <CodeBlock>
                {'"Execute the customer support flow with input: {"message": "I need help with my order"}"'}
              </CodeBlock>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                List available flows:
              </Typography>
              <CodeBlock>
                "List all available flows and show me their descriptions"
              </CodeBlock>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Search for specific flows:
              </Typography>
              <CodeBlock>
                "Search for flows related to data processing or automation"
              </CodeBlock>
            </CardContent>
          </Card>
        </Stack>
      </TabPanel>

      {/* Troubleshooting Tab */}
      <TabPanel value={selectedTab} index={4}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Troubleshooting Guide
            </Typography>
            <Typography variant="body1" paragraph>
              Common issues and their solutions when setting up MCP connections.
            </Typography>
          </Box>

          {troubleshooting.map((item, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center">
                  <WarningIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">{item.issue}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Common Symptoms:
                  </Typography>
                  <List dense>
                    {item.symptoms.map((symptom, symptomIndex) => (
                      <ListItem key={symptomIndex}>
                        <ListItemIcon>
                          <ErrorIcon color="error" />
                        </ListItemIcon>
                        <ListItemText primary={symptom} />
                      </ListItem>
                    ))}
                  </List>

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Solutions:
                  </Typography>
                  <List dense>
                    {item.solutions.map((solution, solutionIndex) => (
                      <ListItem key={solutionIndex}>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary={solution} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Debug Commands
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                Check MCP server status:
              </Typography>
              <CodeBlock>python mcp_client.py websocket --test</CodeBlock>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                View server logs:
              </Typography>
              <CodeBlock>tail -f backend/app.log</CodeBlock>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Test specific flow:
              </Typography>
              <CodeBlock>python mcp_client.py websocket --test --flow-id your_flow_id</CodeBlock>
            </CardContent>
          </Card>
        </Stack>
      </TabPanel>

      {/* Advanced Tab */}
      <TabPanel value={selectedTab} index={5}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Advanced Configuration
            </Typography>
            <Typography variant="body1" paragraph>
              Advanced settings and customization options for MCP connections.
            </Typography>
          </Box>

          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Environment Variables
              </Typography>
              <Typography variant="body2" paragraph>
                Configure your MCP server using these environment variables:
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemText
                    primary={(
                      <Box>
                        <Typography component="code">MCP_LOG_LEVEL</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Set logging level (DEBUG, INFO, WARNING, ERROR)
                        </Typography>
                      </Box>
                    )}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={(
                      <Box>
                        <Typography component="code">MONGODB_URI</Typography>
                        <Typography variant="body2" color="text.secondary">
                          MongoDB connection string
                        </Typography>
                      </Box>
                    )}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={(
                      <Box>
                        <Typography component="code">MCP_MAX_CONCURRENT_EXECUTIONS</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Maximum concurrent flow executions (default: 10)
                        </Typography>
                      </Box>
                    )}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Custom Transport Configuration
              </Typography>
              <Typography variant="body2" paragraph>
                Configure different transport methods for MCP connections:
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                WebSocket Transport:
              </Typography>
              <ConfigBlock
                title="WebSocket Config"
                config={{
                  transport: 'websocket',
                  host: 'localhost',
                  port: 8001,
                  path: '/api/v1/mcp/ws',
                  heartbeat_interval: 30.0,
                  reconnect_attempts: 3,
                }}
              />

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Stdio Transport:
              </Typography>
              <ConfigBlock
                title="Stdio Config"
                config={{
                  transport: 'stdio',
                  server_path: '/path/to/mcp_server.py',
                  timeout: 10.0,
                }}
              />
            </CardContent>
          </Card>

          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Security Considerations
              </Typography>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Important:</strong>
                  {' '}
                  MCP connections should be properly secured in production environments.
                </Typography>
              </Alert>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Use authentication tokens for production MCP servers"
                    secondary="Set MCP_API_KEY environment variable to require authentication"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Restrict network access"
                    secondary="Use firewall rules to limit MCP server access to trusted clients"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Validate flow inputs"
                    secondary="Ensure all flow inputs are validated and sanitized"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Stack>
      </TabPanel>
    </Box>
  );
}

export default MCPHelp;
